import {
	AdfParserStateError,
	AdfUnexpectedDelimiterError,
	AdfUnexpectedTokenError,
} from './errors';
import type { RepairStrategy } from './repair-strategy';
import type { SchemaValidator } from './schema-validator';
import { AdfTokenizer } from './tokenizer';
import type {
	AdfNode,
	ContentNode,
	Mark,
	NodeHandle,
	Repair,
	StreamingCallbacks,
	Token,
	TokenOfType,
	TokenType,
	PrimitiveValue,
	AdfParserConfig,
} from './types';
import { isLiftedNode } from './types';

export const EMPTY_DOC: AdfNode = { type: 'doc', version: 1, content: [] };

// ADF parser with resilient parsing and optional streaming support
// Can handle both complete JSON (parse) and chunked input (processChunk)
export class AdfParser {
	private tokenizer: AdfTokenizer;
	private currentToken: Token;
	private repairs: Repair[] = [];
	private validator: SchemaValidator;
	private repairStrategy: RepairStrategy;
	private hitEofDuringExpect: boolean = false;
	private config: AdfParserConfig;
	// Nodes lifted from simple containers (closeContainer=true) - insert at next valid position
	private immediateLiftedNodes: ContentNode[] = [];
	// Nodes lifted from compound nodes (closeContainer=false) - insert at next valid position
	private accumulatedLiftedNodes: ContentNode[] = [];
	// Tracks the position of a `]` that triggered auto-close, to consume one orphaned `}`
	private autoClosedOnBracketPosition: number = -1;

	// Streaming-specific state
	private callbacks: StreamingCallbacks;
	private currentParentType: string | undefined = undefined;
	private nodeHandles: Map<ContentNode, NodeHandle> = new Map();
	private nodeHandlesByPosition: Map<number, NodeHandle> = new Map(); // Track handles by node position
	private accumulatedInput: string = '';
	private parseComplete: boolean = false;
	private cachedDocument: AdfNode = EMPTY_DOC;
	private lastChunkReceived: boolean = false;

	constructor(options: {
		callbacks?: StreamingCallbacks;
		config?: AdfParserConfig;
		repairStrategy: RepairStrategy;
		schemaValidator: SchemaValidator;
	}) {
		this.validator = options.schemaValidator;
		this.repairStrategy = options.repairStrategy;
		this.callbacks = options.callbacks ?? {};
		this.config = options.config ?? {};

		this.repairStrategy.setAdfParserConfig(this.config);

		// Initialize with empty input - will be set by parse() or processChunk()
		this.tokenizer = new AdfTokenizer('', (repair) => this.recordRepair(repair));
		this.currentToken = this.tokenizer.nextToken();
		this.accumulatedInput = '';
	}

	getRepairs(): Repair[] {
		return this.repairs;
	}

	/**
	 * Process a new chunk of JSON data and return the current partial/complete document.
	 * Use this for incremental parsing as data arrives (e.g., from LLM streaming).
	 *
	 * The parser gracefully handles incomplete JSON by completing open structures.
	 * Each call returns a valid ADF document that can be rendered immediately.
	 *
	 * Note: Once isLastChunk=true is signaled, you must create a new parser instance
	 * to parse another document. Use parse() for multiple documents.
	 *
	 * @param chunk - New JSON data to append
	 * @param isLastChunk - If true, signals this is the final chunk (no more data coming)
	 * @returns The current document (partial or complete)
	 * @throws Error if called after isLastChunk=true was previously signaled
	 */
	processChunk(chunk: string, isLastChunk: boolean = false): AdfNode {
		// Prevent reusing after explicit completion signal
		if (this.lastChunkReceived) {
			throw new AdfParserStateError(
				'Cannot call processChunk() after isLastChunk=true was signaled. ' +
					'Create a new AdfParser instance or use parse() for multiple documents.',
			);
		}

		// Accumulate the chunk
		this.accumulatedInput += chunk;

		// Reset parser state for fresh parse (preserve nodeHandles for cross-chunk tracking)
		this.resetParserState(this.accumulatedInput, false);
		this.tokenizer.reset(this.accumulatedInput, 0);
		this.currentToken = this.tokenizer.nextToken();

		// Parse what we have so far - expect() handles EOF gracefully
		const doc = this.parseDocument();

		// Check if parsing is complete
		if (isLastChunk) {
			this.parseComplete = true;
			this.lastChunkReceived = true;
		} else {
			this.parseComplete = this.currentToken.type === 'EOF' && !this.hitEofDuringExpect;
		}

		this.cachedDocument = doc;
		return doc;
	}

	/**
	 * Get the partial document parsed so far (even if incomplete)
	 * @returns The cached document or null if not yet parsed
	 */
	getPartialDocument(): AdfNode {
		return this.cachedDocument;
	}

	/**
	 * Check if the document is complete
	 * @returns True if all data has been received and parsed
	 */
	isComplete(): boolean {
		return this.parseComplete;
	}

	private countNodesInTree(node: ContentNode): number {
		let count = 1;
		if (Array.isArray(node.marks)) {
			count += node.marks.length;
		}
		if (Array.isArray(node.content)) {
			for (const child of node.content) {
				count += this.countNodesInTree(child);
			}
		}
		return count;
	}

	private recordRepair(repair: Repair): void {
		this.repairs.push(repair);
		this.callbacks.onRepair?.(repair);
	}

	private updateNodeHandle(handle: NodeHandle | undefined, updates: Partial<ContentNode>): void {
		if (handle && this.callbacks.onNodeUpdate) {
			this.callbacks.onNodeUpdate(handle, updates);
		}
	}

	/**
	 * Add lifted node(s) to the appropriate list based on closeContainer flag.
	 * Handles both single nodes and arrays of nodes.
	 */
	private addLiftedNodes(nodes: ContentNode | ContentNode[], closeContainer: boolean): void {
		const targetList = closeContainer ? this.immediateLiftedNodes : this.accumulatedLiftedNodes;
		if (Array.isArray(nodes)) {
			targetList.push(...nodes);
		} else {
			targetList.push(nodes);
		}
	}

	/**
	 * Sanitize a node type name for use in repair records.
	 * Returns the type if valid, otherwise returns a truncated unknown_prefix.
	 */
	private sanitizeNodeTypeName(type?: string): string {
		if (!type) {
			return 'unknown';
		}
		return this.validator.isValidNodeType(type) ? type : `unknown`;
	}

	/**
	 * Sanitize a mark type name for use in repair records.
	 * Returns the type if valid, otherwise returns a truncated unknown_prefix.
	 */
	private sanitizeMarkTypeName(type?: string): string {
		if (!type) {
			return 'unknown';
		}
		return this.validator.isValidMarkType(type) ? type : `unknown`;
	}

	private addRepairedNode(
		repairedNode: ContentNode | ContentNode[],
		content: ContentNode[],
		originalNode: ContentNode,
		context: string,
	): void {
		if (Array.isArray(repairedNode) && repairedNode.length === 0) {
			return;
		}

		const originalNodeCount = this.countNodesInTree(originalNode);
		const repairedNodeCount = Array.isArray(repairedNode)
			? repairedNode.reduce((sum, n) => sum + this.countNodesInTree(n), 0)
			: this.countNodesInTree(repairedNode);

		const isUnwrap = Array.isArray(repairedNode);
		const expansion = repairedNodeCount - originalNodeCount;
		const repairedType = isUnwrap ? '❌' : repairedNode.type || '❌';

		// For unwrap: check if the original node is preserved in the array
		const nodePreserved =
			isUnwrap &&
			Array.isArray(repairedNode) &&
			repairedNode.length === 1 &&
			repairedNode[0]?.type === originalNode.type;

		const repair: Repair = {
			type: isUnwrap ? 'unwrap' : 'wrap',
			// This is safe since we only `wrap` or `unwrap` nodes that are valid
			from: originalNode.type,
			to: repairedType,
			context,
			expansion,
			nodePreserved: isUnwrap ? nodePreserved : undefined,
		};

		this.recordRepair(repair);

		if (Array.isArray(repairedNode)) {
			content.push(...repairedNode);
		} else {
			content.push(repairedNode);
		}
	}

	/**
	 * Try to append a node to the content array, applying repairs if needed.
	 * @returns true if lift mode should be enabled (close container), false otherwise
	 */
	private tryAppendNodeToContent(
		parentType: string | undefined,
		node: ContentNode,
		content: ContentNode[],
		isValidChild: (childType: string) => boolean,
		options?: { topNodeType?: string },
	): boolean {
		const topNodeType = options?.topNodeType || this.validator.getTopNodeType();

		// If node has no type, try to let repair strategy fix it
		if (!node?.type) {
			const repairedNode = this.repairStrategy.tryRepair(node, parentType, content, true);

			// Check if type was inferred (node mutated in place)
			if (node.type) {
				this.recordRepair({
					type: 'infer',
					from: 'missing_type',
					to: node.type,
					context: `inferred_from_properties:${parentType || topNodeType}`,
				});
			} else if (repairedNode) {
				// Handle lifted nodes
				if (isLiftedNode(repairedNode)) {
					const closeContainer = repairedNode.closeContainer ?? false;
					this.addLiftedNodes(repairedNode.node, closeContainer);
					this.recordRepair({
						type: 'lift',
						from: this.sanitizeNodeTypeName(node.type),
						to: 'parent',
						context: parentType || topNodeType,
					});
					return closeContainer;
				}
				this.addRepairedNode(repairedNode, content, node, parentType || topNodeType);
				return false;
			} else {
				// Still no type after repair - skip it
				return false;
			}
		}

		// Handle empty text nodes (ProseMirror doesn't allow them)
		if (node.type === 'text' && (!node.text || node.text.length === 0)) {
			this.recordRepair({
				type: 'skip_empty_text',
				from: 'text',
				to: '-',
				context: parentType || topNodeType,
			});

			// Also account for marks on the empty text node
			if (Array.isArray(node.marks)) {
				for (const mark of node.marks) {
					if (mark.type) {
						this.recordRepair({
							type: 'skip_empty_text',
							from: this.sanitizeMarkTypeName(mark.type),
							to: '-',
							context: `${parentType || topNodeType}:text`,
						});
					}
				}
			}
			return false;
		}

		// Handle empty listItem/taskItem nodes whose content was lifted out.
		// Only skip when immediateLiftedNodes is non-empty
		// (indicating lifting actually occurred from this node, not just an empty streaming node)
		if (
			(node.type === 'listItem' || node.type === 'taskItem') &&
			(!node.content || node.content.length === 0) &&
			this.immediateLiftedNodes.length > 0
		) {
			this.recordRepair({
				type: 'skip',
				from: node.type,
				to: '-',
				context: `${parentType || topNodeType}:empty_after_lift`,
			});
			return false;
		}

		// Apply mutations to all nodes (fix timestamps, infer types, etc.)
		this.repairStrategy.applyNodeMutations(node);

		// Check validity first
		const isValid = isValidChild(node.type);
		const needsStructuralFix = this.repairStrategy.shouldAttemptStructuralFix(node, parentType);

		if (!isValid || needsStructuralFix) {
			// Delegate all repairs to the repair strategy
			const repairedNode = this.repairStrategy.tryRepair(node, parentType, content, !isValid);
			if (repairedNode) {
				// Handle lifted nodes - add to appropriate list based on closeContainer
				if (isLiftedNode(repairedNode)) {
					const closeContainer = repairedNode.closeContainer ?? false;
					this.addLiftedNodes(repairedNode.node, closeContainer);
					this.recordRepair({
						type: 'lift',
						from: this.sanitizeNodeTypeName(node.type),
						to: 'parent',
						context: parentType || topNodeType,
					});
					return closeContainer;
				}
				this.addRepairedNode(repairedNode, content, node, parentType || topNodeType);
				return false;
			}

			this.recordRepair({
				type: 'skip',
				from: this.sanitizeNodeTypeName(node.type),
				to: '-',
				context: parentType || topNodeType,
			});
			return false;
		}
		content.push(node);
		return false;
	}

	private advance(): void {
		this.currentToken = this.tokenizer.nextToken();
	}

	private peekType<T extends TokenType>(...types: T[]): boolean {
		return (types as readonly TokenType[]).includes(this.currentToken.type);
	}

	private isStartOfStructure(type: TokenType): boolean {
		return type === 'LEFT_BRACE' || type === 'LEFT_BRACKET';
	}

	private isPrimitiveToken(type: TokenType): boolean {
		return type === 'STRING' || type === 'NUMBER' || type === 'BOOLEAN' || type === 'NULL';
	}

	// Check if token type could start an object property value
	private isObjectPropertyValueStart(type: TokenType): boolean {
		return (
			// Normal: "key": value
			type === 'COLON' ||
			// Missing colon: "key" "value"
			this.isPrimitiveToken(type) ||
			// Missing colon: "key" {...}
			this.isStartOfStructure(type)
		);
	}

	private isObjectStart(): boolean {
		if (this.currentToken.type !== 'STRING') {
			return false;
		}
		const savedPosition = this.tokenizer.getPosition();
		const savedToken = this.currentToken;

		const nextToken = this.tokenizer.nextToken();
		const isObject = this.isObjectPropertyValueStart(nextToken.type);

		this.tokenizer.reset(this.accumulatedInput, savedPosition);
		this.currentToken = savedToken;

		return isObject;
	}

	/**
	 * Process remaining lifted nodes at the end of a content array.
	 * Both immediate and accumulated nodes are primarily processed inline during parsing,
	 * but this handles any that couldn't be inserted and need to bubble up.
	 * At root level, invalid nodes are repaired (wrapped) instead of being lost.
	 * @param skipImmediateProcessing If true, skip processing immediate nodes (they all bubble up).
	 *        This is used when liftMode was active - remaining siblings should go to parent, not back to current container.
	 */
	private processLiftedNodes(
		parentType: string | undefined,
		content: ContentNode[],
		isValidChild: (childType: string) => boolean,
		immediateCountAtStart: number,
		accumulatedCountAtStart: number,
		skipImmediateProcessing: boolean = false,
	): void {
		const isRootLevel = parentType === this.validator.getTopNodeType();

		// Process immediate lifted nodes first - these should appear right after their parent container
		// Process in order (not reverse) since they were pushed in order
		// If skipImmediateProcessing is true (liftMode was active), all nodes bubble up
		const immediateStillLifted: ContentNode[] = [];
		const immediateToProcess = this.immediateLiftedNodes.splice(immediateCountAtStart);
		for (const lifted of immediateToProcess) {
			if (!skipImmediateProcessing && isValidChild(lifted.type)) {
				content.push(lifted);
			} else if (isRootLevel) {
				// At root level, try to repair (wrap) the node instead of losing it
				this.tryAppendNodeToContent(parentType, lifted, content, isValidChild, {
					topNodeType: parentType,
				});
			} else {
				immediateStillLifted.push(lifted);
			}
		}
		// Put back any immediate nodes that couldn't be added at this level
		this.immediateLiftedNodes.push(...immediateStillLifted);

		// Process remaining accumulated lifted nodes that couldn't be inserted inline
		const accumulatedStillLifted: ContentNode[] = [];
		const accumulatedToProcess = this.accumulatedLiftedNodes.splice(accumulatedCountAtStart);
		for (const lifted of accumulatedToProcess) {
			if (isValidChild(lifted.type)) {
				content.push(lifted);
			} else if (isRootLevel) {
				// At root level, try to repair (wrap) the node instead of losing it
				this.tryAppendNodeToContent(parentType, lifted, content, isValidChild, {
					topNodeType: parentType,
				});
			} else {
				accumulatedStillLifted.push(lifted);
			}
		}
		// Put back any accumulated nodes that couldn't be added at this level
		this.accumulatedLiftedNodes.push(...accumulatedStillLifted);
	}

	/**
	 * Process lifted nodes inline during content array parsing.
	 * Inserts valid nodes into content in FIFO order, stops at first invalid node.
	 * @param liftedNodes The lifted nodes array to process (immediate or accumulated)
	 * @param content The content array to append valid nodes to
	 * @param isValidChild Validator function to check if a node type is valid
	 * @param startIndex The index in liftedNodes to start processing from
	 */
	private processLiftedNodesInline(
		liftedNodes: ContentNode[],
		content: ContentNode[],
		isValidChild: (type: string) => boolean,
		startIndex: number,
	): void {
		while (liftedNodes.length > startIndex) {
			const lifted = liftedNodes[startIndex];
			if (lifted && isValidChild(lifted.type)) {
				liftedNodes.splice(startIndex, 1);
				content.push(lifted);
			} else {
				break;
			}
		}
	}

	// Consume and advance if token type matches, otherwise return null
	private consume<T extends TokenType>(type: T): TokenOfType<T> | null {
		if (this.currentToken.type === type) {
			const tok = this.currentToken as TokenOfType<T>;
			this.advance();
			return tok;
		}
		return null;
	}

	private expectAndAdvance(tokenType: TokenType): void {
		this.expect(tokenType);
		if (this.currentToken.type === tokenType) {
			this.advance();
		}
	}

	private getTryAppendOptions() {
		return {
			topNodeType: this.currentParentType || this.validator.getTopNodeType(),
		};
	}

	// Execute fn within parent context scope, then restore previous parent
	private withParentContext<T>(parentType: string | undefined, fn: () => T): T {
		const previousParent = this.currentParentType;
		this.currentParentType = parentType;
		try {
			return fn();
		} finally {
			this.currentParentType = previousParent;
		}
	}

	private expectOptionalDelimiter(
		delimiter: 'LEFT_BRACE' | 'LEFT_BRACKET',
		context: string,
	): boolean {
		if (this.peekType(delimiter)) {
			this.advance();
			return true;
		} else if (this.peekType('STRING')) {
			this.recordRepair({
				type: 'skip',
				from: `missing_${delimiter}`,
				to: 'inferred',
				context,
			});

			return false;
		} else if (this.peekType('EOF')) {
			this.expect(delimiter);
			return false;
		} else if (this.peekType('LEFT_BRACE') && delimiter === 'LEFT_BRACKET') {
			return false;
		} else {
			throw new AdfUnexpectedDelimiterError(
				delimiter,
				this.currentToken.type,
				this.currentToken.position,
			);
		}
	}

	private expect(tokenType: TokenType): void {
		if (this.currentToken.type !== tokenType) {
			if (this.currentToken.type === 'EOF') {
				this.hitEofDuringExpect = true;

				this.recordRepair({
					type: 'skip',
					from: `expected_${tokenType}`,
					to: 'EOF',
					context: 'streaming',
				});
				return;
			} else {
				throw new AdfUnexpectedTokenError(
					tokenType,
					this.currentToken.type,
					this.currentToken.position,
				);
			}
		}
	}

	private parsePrimitiveValue(): PrimitiveValue {
		if (this.currentToken.type === 'STRING') {
			const value = this.currentToken.value;
			this.advance();
			return value;
		} else if (this.currentToken.type === 'NUMBER') {
			const value = Number(this.currentToken.value);
			this.advance();
			return value;
		} else if (this.currentToken.type === 'BOOLEAN') {
			const value = this.currentToken.value === 'true';
			this.advance();
			return value;
		} else if (this.currentToken.type === 'NULL') {
			this.advance();
			return null;
		} else {
			this.skipValue();
			return undefined;
		}
	}

	private parseObjectProperties(handler: (key: string) => void, context: string = 'object'): void {
		const hasOpeningBrace = this.expectOptionalDelimiter('LEFT_BRACE', context);
		if (!hasOpeningBrace && this.peekType('EOF')) {
			return;
		}

		const processedKeys = new Set<string>();

		while (!this.peekType('RIGHT_BRACE') && !this.peekType('EOF')) {
			// Handle dangling `]` - likely means the object is missing its closing `}`
			// and the `]` belongs to a parent array. Auto-close the object.
			// But if we've already parsed 'content', the `]` is likely from a parent array, not a missing `}`.
			if (this.peekType('RIGHT_BRACKET') && !processedKeys.has('content')) {
				this.recordRepair({
					type: 'infer',
					from: 'missing_closing_brace_on_object',
					to: 'inferred',
					context,
				});
				// Track position of this `]` so parent can consume one orphaned `}` after it.
				// Only set if not already set for this same `]` (avoids double-counting at nested levels).
				if (this.autoClosedOnBracketPosition !== this.currentToken.position) {
					this.autoClosedOnBracketPosition = this.currentToken.position;
				}
				// Don't consume the `]` - let the parent array handle it
				return;
			}

			if (!this.peekType('STRING')) {
				this.skipValue();
				continue;
			}
			const keyToken = this.consume('STRING');
			const key = keyToken?.value as string;

			if (this.consume('COMMA')) {
				const from = this.sanitizeNodeTypeName(key);
				this.recordRepair({
					type: 'skip',
					from,
					to: '-',
					context: `${context}.${from}:empty_value`,
				});
				continue;
			}

			if (this.consume('COLON')) {
				// value follows
			} else if (this.peekType('EOF')) {
				continue;
			} else {
				this.recordRepair({
					type: 'skip',
					from: 'missing_colon',
					to: 'inferred',
					context: `${context}.${key}`,
				});
			}

			if (processedKeys.has(key)) {
				this.recordRepair({
					type: 'duplicate_key',
					from: this.sanitizeNodeTypeName(key),
					to: 'overwritten',
					context,
				});
			}
			processedKeys.add(key);

			handler(key);

			this.consume('COMMA');
		}

		this.expect('RIGHT_BRACE');
		this.consume('RIGHT_BRACE');
	}

	private skipValue(): void {
		switch (this.currentToken.type) {
			case 'STRING':
			case 'NUMBER':
			case 'BOOLEAN':
			case 'NULL':
				this.advance();
				break;
			case 'LEFT_BRACE':
				this.skipObject();
				break;
			case 'LEFT_BRACKET':
				this.skipArray();
				break;
			case 'RIGHT_BRACE':
			case 'RIGHT_BRACKET':
			case 'COMMA':
			case 'COLON':
				this.recordRepair({
					type: 'skip',
					from: this.currentToken.type,
					to: '-',
					context: 'parser',
				});
				this.advance();
				break;
			case 'EOF':
				this.recordRepair({
					type: 'skip',
					from: this.currentToken.type,
					to: '-',
					context: 'parser',
				});
				break;
			default: {
				const tok = this.currentToken as Token;
				this.recordRepair({ type: 'skip', from: tok.type, to: '-', context: 'parser' });
				this.advance();
				break;
			}
		}
	}

	private skipDelimitedSequence(
		openDelimiter: 'LEFT_BRACE' | 'LEFT_BRACKET',
		closeDelimiter: 'RIGHT_BRACE' | 'RIGHT_BRACKET',
		skipItem: () => void,
	): void {
		this.expectAndAdvance(openDelimiter);

		while (this.currentToken.type !== closeDelimiter && this.currentToken.type !== 'EOF') {
			skipItem();
			if (this.currentToken.type === 'COMMA') {
				this.advance();
			}
		}

		this.expectAndAdvance(closeDelimiter);
	}

	private skipObject(): void {
		this.skipDelimitedSequence('LEFT_BRACE', 'RIGHT_BRACE', () => {
			this.expect('STRING');
			this.advance();
			this.expect('COLON');
			this.advance();
			this.skipValue();
		});
	}

	private skipArray(): void {
		this.skipDelimitedSequence('LEFT_BRACKET', 'RIGHT_BRACKET', () => {
			this.skipValue();
		});
	}

	private parseArray<T>(parseItem: () => T | null, onItem: (item: T) => void): void {
		this.expectAndAdvance('LEFT_BRACKET');
		while (!this.peekType('RIGHT_BRACKET') && !this.peekType('EOF')) {
			if (this.consume('COMMA')) {
				continue;
			}
			const item = parseItem();
			if (item !== null) {
				onItem(item);
			}
		}
		this.expectAndAdvance('RIGHT_BRACKET');
	}

	/**
	 * State container for content array parsing.
	 * Groups related state variables to reduce parameter passing.
	 */
	private initContentParseState() {
		return {
			content: [] as ContentNode[],
			liftMode: false,
			immediateCountAtStart: this.immediateLiftedNodes.length,
			accumulatedCountAtStart: this.accumulatedLiftedNodes.length,
		};
	}

	/**
	 * Determine if the content parsing loop should continue.
	 * @param isMalformed Whether the array is malformed (missing brackets)
	 */
	private shouldContinueContentParsing(isMalformed: boolean): boolean {
		if (isMalformed) {
			return this.peekType('LEFT_BRACE') || this.isObjectStart();
		}
		return !this.peekType('RIGHT_BRACKET') && !this.peekType('EOF');
	}

	/**
	 * Handle delimiter consumption in malformed array mode.
	 * @returns true if parsing should break out of the loop, false to continue
	 */
	private handleMalformedArrayDelimiters(): boolean {
		if (this.consume('COMMA')) {
			return false; // continue
		}
		if (this.consume('RIGHT_BRACE')) {
			if (!this.peekType('LEFT_BRACE')) {
				return true; // break
			}
			return false; // continue
		}
		if (this.peekType('LEFT_BRACE')) {
			return false; // continue
		}
		return true; // break
	}

	/**
	 * Check for implicit container breaks and handle them.
	 * @returns Object with liftMode flag, or null if no break needed
	 */
	private checkImplicitContainerBreak(
		node: ContentNode,
		parentType: string | undefined,
	): { liftMode: boolean } | null {
		if (!this.repairStrategy.shouldAttemptStructuralFix(node, parentType)) {
			return null;
		}
		// Only break out of non-root containers
		if (parentType === this.validator.getTopNodeType()) {
			return null;
		}

		this.recordRepair({
			type: 'unwrap',
			from: 'implicit_container_end',
			to: 'break_container',
			context: parentType || 'unknown',
		});
		this.immediateLiftedNodes.push(node);
		return { liftMode: true };
	}

	/**
	 * Process a single content array item (node) and update state.
	 */
	private processContentArrayNode(
		node: ContentNode,
		parentType: string | undefined,
		isValidChild: (type: string) => boolean,
		state: ReturnType<typeof this.initContentParseState>,
		isMalformed: boolean,
	): void {
		// If we're in lift mode, remaining siblings go to immediate list
		if (state.liftMode) {
			this.immediateLiftedNodes.push(node);
			return;
		}

		// Check for implicit container breaks (only in normal array mode)
		if (!isMalformed) {
			const breakResult = this.checkImplicitContainerBreak(node, parentType);
			if (breakResult) {
				state.liftMode = true;
				return;
			}
		}

		// Try to append node to content, applying repairs if needed
		const shouldCloseContainer = this.tryAppendNodeToContent(
			parentType,
			node,
			state.content,
			isValidChild,
			this.getTryAppendOptions(),
		);

		// Enter lift mode if closeContainer was signaled
		if (shouldCloseContainer) {
			state.liftMode = true;
		}

		// Process lifted nodes inline
		this.processLiftedNodesInline(
			this.immediateLiftedNodes,
			state.content,
			isValidChild,
			state.immediateCountAtStart,
		);

		// Also process accumulated lifted nodes inline in normal array mode
		if (!isMalformed) {
			this.processLiftedNodesInline(
				this.accumulatedLiftedNodes,
				state.content,
				isValidChild,
				state.accumulatedCountAtStart,
			);
		}
	}

	/**
	 * Finalize content array parsing: handle closing bracket and orphaned braces.
	 */
	private finalizeContentArray(
		parentType: string | undefined,
		state: ReturnType<typeof this.initContentParseState>,
		isValidChild: (type: string) => boolean,
		isMalformed: boolean,
	): void {
		// Handle closing bracket for normal arrays
		if (!isMalformed) {
			this.expectAndAdvance('RIGHT_BRACKET');

			// Handle orphaned closing brace from auto-closed objects
			if (this.autoClosedOnBracketPosition >= 0 && this.peekType('RIGHT_BRACE')) {
				this.recordRepair({
					type: 'skip',
					from: 'orphaned_closing_brace',
					to: '-',
					context: parentType || 'content',
				});
				this.advance();
				this.autoClosedOnBracketPosition = -1;
			}
		}

		// Process any remaining lifted nodes
		this.processLiftedNodes(
			parentType,
			state.content,
			isValidChild,
			state.immediateCountAtStart,
			state.accumulatedCountAtStart,
			state.liftMode,
		);
	}

	/**
	 * Handle error cases when content array cannot be parsed.
	 */
	private handleContentArrayError(parentType?: string): ContentNode[] {
		if (this.peekType('EOF')) {
			this.expect('LEFT_BRACKET');
			return [];
		}

		// Unexpected token
		this.recordRepair({
			type: 'skip',
			from: this.currentToken.type,
			to: 'empty_array',
			context: parentType || 'content',
		});
		this.skipValue();
		return [];
	}

	/**
	 * Parse a content array — original two-path implementation (V1).
	 * Kept as the default while the unified V2 implementation is rolled out.
	 */
	private parseContentArrayV1(parentType?: string): ContentNode[] {
		const isValidChild = this.validator.isValidChild(parentType);

		// Case 1: Malformed - missing array brackets
		if (this.peekType('LEFT_BRACE')) {
			this.recordRepair({
				type: 'skip',
				from: 'missing_brackets',
				to: 'inferred_array',
				context: parentType || 'content',
			});
			const content: ContentNode[] = [];
			let liftMode = false;
			const immediateCountAtStart = this.immediateLiftedNodes.length;
			const accumulatedCountAtStart = this.accumulatedLiftedNodes.length;

			while (this.peekType('LEFT_BRACE') || this.isObjectStart()) {
				const node = this.parseContentNode();
				if (node) {
					if (liftMode) {
						// In lift mode, remaining siblings go to immediate list
						this.immediateLiftedNodes.push(node);
					} else {
						const shouldCloseContainer = this.tryAppendNodeToContent(
							parentType,
							node,
							content,
							isValidChild,
							this.getTryAppendOptions(),
						);
						if (shouldCloseContainer) {
							liftMode = true;
						}

						// Process immediate lifted nodes right after each node is added (FIFO order)
						this.processLiftedNodesInline(
							this.immediateLiftedNodes,
							content,
							isValidChild,
							immediateCountAtStart,
						);
					}
				}
				if (this.consume('COMMA')) {
					// continue
				} else if (this.consume('RIGHT_BRACE')) {
					if (!this.peekType('LEFT_BRACE')) {
						break;
					}
				} else if (this.peekType('LEFT_BRACE')) {
					// continue
				} else {
					break;
				}
			}

			// Process lifted nodes - immediate ones first (in order), then accumulated (at end)
			// If liftMode is active, skip re-evaluating immediate nodes - they all bubble up
			this.processLiftedNodes(
				parentType,
				content,
				isValidChild,
				immediateCountAtStart,
				accumulatedCountAtStart,
				liftMode,
			);

			return content;
		}

		// Case 2: Normal array
		if (this.consume('LEFT_BRACKET')) {
			const content: ContentNode[] = [];
			let liftMode = false; // Once we lift a node (closeContainer=true), lift all subsequent siblings
			const immediateCountAtStart = this.immediateLiftedNodes.length;
			const accumulatedCountAtStart = this.accumulatedLiftedNodes.length;

			while (!this.peekType('RIGHT_BRACKET') && !this.peekType('EOF')) {
				if (this.consume('COMMA') || this.consume('RIGHT_BRACE')) {
					continue;
				}

				if (this.peekType('LEFT_BRACE') || this.isObjectStart()) {
					const node = this.parseContentNode();
					if (node) {
						// If we're in lift mode (closeContainer was triggered), add remaining to immediate list
						if (liftMode) {
							this.immediateLiftedNodes.push(node);
							continue;
						}

						// Check for implicit container breaks (e.g. missing closing bracket)
						if (this.repairStrategy.shouldAttemptStructuralFix(node, parentType)) {
							// If we're not at the root level, structural fixes usually mean we should
							// break out of the current container (e.g. valid heading inside a table -> table ends).
							// If we ARE at the root, we can't break out, so we'll rely on tryAppendNodeToContent to wrap/repair in place.
							if (parentType !== this.validator.getTopNodeType()) {
								this.recordRepair({
									type: 'unwrap',
									from: 'implicit_container_end',
									to: 'break_container',
									context: parentType || 'unknown',
								});
								// Add the invalid node to immediate lifted nodes for parent to handle
								this.immediateLiftedNodes.push(node);
								// Enter lift mode to collect remaining siblings
								liftMode = true;
								continue;
							}
						}

						const shouldCloseContainer = this.tryAppendNodeToContent(
							parentType,
							node,
							content,
							isValidChild,
							this.getTryAppendOptions(),
						);

						// If closeContainer was signaled, enter lift mode for remaining siblings
						// This happens for simple containers (panel, blockquote) - not for compound nodes (list, table)
						if (shouldCloseContainer) {
							liftMode = true;
						}

						// Process immediate lifted nodes right after each node is added (FIFO order)
						// This ensures nodes lifted from nested content appear immediately after their parent container
						this.processLiftedNodesInline(
							this.immediateLiftedNodes,
							content,
							isValidChild,
							immediateCountAtStart,
						);

						// Also process accumulated lifted nodes inline (from compound containers like list/table)
						// This inserts them at the next valid position rather than waiting until the end
						this.processLiftedNodesInline(
							this.accumulatedLiftedNodes,
							content,
							isValidChild,
							accumulatedCountAtStart,
						);
					}
				} else {
					this.skipValue();
				}
			}

			this.expectAndAdvance('RIGHT_BRACKET');

			// When we auto-closed an object on encountering a `]`, consume the orphaned `}` token
			// that was meant for the auto-closed object.
			// This handles malformed JSON like `}]}` where `]` appears before the object's closing `}`.
			if (this.autoClosedOnBracketPosition >= 0 && this.peekType('RIGHT_BRACE')) {
				this.recordRepair({
					type: 'skip',
					from: 'orphaned_closing_brace',
					to: '-',
					context: parentType || 'content',
				});
				this.advance();
				this.autoClosedOnBracketPosition = -1;
			}

			// Process any remaining lifted nodes that couldn't be inserted inline
			// If liftMode is active, skip re-evaluating immediate nodes - they all bubble up
			this.processLiftedNodes(
				parentType,
				content,
				isValidChild,
				immediateCountAtStart,
				accumulatedCountAtStart,
				liftMode,
			);

			return content;
		}

		// Case 3: EOF
		if (this.peekType('EOF')) {
			this.expect('LEFT_BRACKET');
			return [];
		}

		// Case 4: Unexpected token
		this.recordRepair({
			type: 'skip',
			from: this.currentToken.type,
			to: 'empty_array',
			context: parentType || 'content',
		});
		this.skipValue();
		return [];
	}

	/**
	 * Dispatch to the correct parseContentArray implementation based on config.
	 * Defaults to V1; set `useUnifiedContentParsing: true` to use V2.
	 */
	private parseContentArray(parentType?: string): ContentNode[] {
		if (this.config.useUnifiedContentParsing) {
			return this.parseContentArrayV2(parentType);
		}
		return this.parseContentArrayV1(parentType);
	}

	/**
	 * Parse a content array, handling both malformed (missing brackets) and normal arrays.
	 * Unified implementation that reduces code duplication between the two cases (V2).
	 */
	private parseContentArrayV2(parentType?: string): ContentNode[] {
		const isValidChild = this.validator.isValidChild(parentType);

		// Detect array format: malformed (missing brackets) vs normal
		const isMalformed = this.peekType('LEFT_BRACE');

		if (isMalformed) {
			// Record repair for missing brackets
			this.recordRepair({
				type: 'skip',
				from: 'missing_brackets',
				to: 'inferred_array',
				context: parentType || 'content',
			});
		} else if (!this.consume('LEFT_BRACKET')) {
			// Handle EOF and unexpected token cases
			return this.handleContentArrayError(parentType);
		}

		// Initialize parsing state
		const state = this.initContentParseState();

		// Unified parsing loop
		while (this.shouldContinueContentParsing(isMalformed)) {
			// Handle delimiters in normal array mode
			if (!isMalformed && (this.consume('COMMA') || this.consume('RIGHT_BRACE'))) {
				continue;
			}

			// Parse content node if we're at an object start
			if (this.peekType('LEFT_BRACE') || this.isObjectStart()) {
				const node = this.parseContentNode();

				if (node) {
					this.processContentArrayNode(node, parentType, isValidChild, state, isMalformed);
				}

				// Handle delimiters in malformed array mode (after node parsing)
				if (isMalformed && this.handleMalformedArrayDelimiters()) {
					break;
				}
			} else if (!isMalformed) {
				this.skipValue();
			} else {
				// In malformed mode, if not at object start, break
				break;
			}
		}

		// Finalize and return
		this.finalizeContentArray(parentType, state, isValidChild, isMalformed);
		return state.content;
	}

	private parseMarks(): Mark[] {
		const marks: Mark[] = [];
		this.parseArray<Mark>(
			() => {
				if (this.currentToken.type !== 'LEFT_BRACE') {
					this.skipValue();
					return null;
				}
				const mark: Partial<Mark> = {};
				this.parseObjectProperties((key) => {
					if (key === 'type') {
						if (this.currentToken.type === 'STRING') {
							mark.type = this.currentToken.value;
							this.advance();
						} else {
							this.skipValue();
						}
					} else if (key === 'attrs') {
						if (this.currentToken.type === 'LEFT_BRACE') {
							mark.attrs = this.parseAttrs();
						} else {
							this.skipValue();
						}
					} else {
						this.skipValue();
					}
				}, 'marks');
				return mark.type ? (mark as Mark) : null;
			},
			(m) => marks.push(m),
		);
		return marks;
	}

	private parseAttrs(): Record<string, PrimitiveValue> {
		const attrs: Record<string, PrimitiveValue> = {};
		this.parseObjectProperties((key) => {
			attrs[key] = this.parsePrimitiveValue();
		}, 'attrs');
		return attrs;
	}

	private parseRootNode(): AdfNode {
		const node: Partial<AdfNode> = {};

		this.parseObjectProperties((key) => {
			if (key === 'version') {
				if (this.currentToken.type === 'NUMBER') {
					node.version = Number(this.currentToken.value);
					this.advance();
				} else {
					this.skipValue();
				}
			} else if (key === 'type') {
				if (this.currentToken.type === 'STRING') {
					node.type = this.currentToken.value;
					this.advance();
				} else {
					this.skipValue();
				}
			} else if (key === 'content') {
				node.content = this.withParentContext(node.type, () => this.parseContentArray(node.type));
			} else if (key === 'attrs') {
				if (this.currentToken.type === 'LEFT_BRACE') {
					node.attrs = this.parseAttrs();
				} else {
					this.skipValue();
				}
			} else if (key === 'text') {
				if (this.currentToken.type === 'STRING') {
					node.text = this.currentToken.value;
					this.advance();
				} else {
					this.skipValue();
				}
			} else if (key === 'marks') {
				if (this.currentToken.type === 'LEFT_BRACKET') {
					node.marks = this.parseMarks();
				} else {
					this.skipValue();
				}
			} else {
				const value = this.parsePrimitiveValue();
				node[key] = value;
			}
		}, node.type || 'root');

		// If no type specified, infer it as the top node type
		if (!node.type) {
			const topNodeType = this.validator.getTopNodeType();
			node.type = topNodeType;

			this.recordRepair({
				type: 'infer',
				from: 'missing_type',
				to: topNodeType,
				context: 'root',
			});
		}

		// Apply mutations to root node (fix timestamps, etc.)
		this.repairStrategy.applyNodeMutations(node as AdfNode);

		// Default content to empty array for non-leaf nodes if not provided
		if (node.content === undefined && !this.validator.isLeafNode(node.type)) {
			node.content = [];
		}

		return node as AdfNode;
	}

	private parseContentNode(): ContentNode | null {
		const node: Partial<ContentNode> = {};
		let handle: NodeHandle | undefined;
		let nodeStartPosition: number | undefined;

		this.parseObjectProperties((key) => {
			if (key === 'type') {
				this.expect('STRING');
				nodeStartPosition = this.currentToken.position;
				node.type = this.currentToken.value;
				this.advance();

				// Early fuzzy matching: correct unknown types before children are parsed
				// so that children see a valid parent context for their own fuzzy matching.
				if (this.config.fuzzyNodeTypeMatching && node.type) {
					const match = this.repairStrategy.tryFuzzyMatchType(node.type);
					if (match) {
						this.recordRepair({
							type: 'infer',
							from: 'unknown',
							to: match.correctedType,
							context: `fuzzy_match:d=${match.distance}:max=${match.maxDistance}`,
						});
						node.type = match.correctedType;
					}
				}

				// Check if we've already seen this node (by position) in a previous chunk
				const existingHandle = this.nodeHandlesByPosition.get(nodeStartPosition);
				if (existingHandle) {
					// Reuse existing handle for updates
					handle = existingHandle;
				} else if (this.callbacks.onNodeStart && node.type) {
					// First time seeing this node - call onNodeStart
					handle = this.callbacks.onNodeStart(node.type, this.currentParentType);
					if (handle !== undefined) {
						this.nodeHandlesByPosition.set(nodeStartPosition, handle);
					}
				}
			} else if (key === 'content') {
				node.content = this.withParentContext(node.type, () => this.parseContentArray(node.type));

				this.updateNodeHandle(handle, { content: node.content });
			} else if (key === 'attrs') {
				if (this.currentToken.type === 'LEFT_BRACE') {
					node.attrs = this.parseAttrs();
					this.updateNodeHandle(handle, { attrs: node.attrs });
				} else {
					this.skipValue();
				}
			} else if (key === 'text') {
				if (this.currentToken.type === 'STRING') {
					node.text = this.currentToken.value;
					this.advance();
					this.updateNodeHandle(handle, { text: node.text });
				} else {
					this.skipValue();
				}
			} else if (key === 'marks') {
				if (this.currentToken.type === 'LEFT_BRACKET') {
					node.marks = this.parseMarks();
					this.updateNodeHandle(handle, { marks: node.marks });
				} else {
					this.skipValue();
				}
			} else {
				const value = this.parsePrimitiveValue();
				node[key] = value;
				if (value !== undefined) {
					this.updateNodeHandle(handle, { [key]: value });
				}
			}
		}, 'node');

		// Let repair strategy handle missing types
		const completeNode = node as ContentNode;

		if (completeNode && handle) {
			this.callbacks.onNodeComplete?.(handle, completeNode);
			this.nodeHandles.set(completeNode, handle);
		}

		return completeNode;
	}

	private resetParserState(input: string, clearNodeHandles: boolean = true): void {
		this.repairs = [];
		this.accumulatedInput = input;
		this.parseComplete = false;
		this.cachedDocument = EMPTY_DOC;
		this.hitEofDuringExpect = false;
		this.immediateLiftedNodes = [];
		this.accumulatedLiftedNodes = [];
		this.autoClosedOnBracketPosition = -1;
		if (clearNodeHandles) {
			this.nodeHandles.clear();
		}
	}

	/**
	 * Parse a complete JSON string into an ADF document.
	 * This is for synchronous, non-streaming parsing.
	 *
	 * @param input - The complete JSON string to parse
	 * @returns The parsed ADF document
	 */
	parse(input: string): AdfNode {
		// Reset parser state for new document
		this.resetParserState(input);

		// Reset tokenizer with new input
		this.tokenizer.reset(input, 0);
		this.currentToken = this.tokenizer.nextToken();

		const doc = this.parseDocument();

		this.parseComplete = true;
		this.cachedDocument = doc;
		return doc;
	}

	// Internal: shared parsing logic (expects tokenizer to be initialized)
	private parseDocument(): AdfNode {
		const rootNode = this.parseRootNode();

		// If root node has content, validate it
		if (Array.isArray(rootNode.content)) {
			const isValidChild = this.validator.isValidChild(rootNode.type);
			for (const node of rootNode.content) {
				if (node.type) {
					isValidChild(node.type);
				}
			}
		}

		// Handle any additional nodes after the root (for multiple top-level objects)
		const allContent = Array.isArray(rootNode.content) ? [...rootNode.content] : [];
		const isValidChild = this.validator.isValidChild(rootNode.type);

		while (this.currentToken.type !== 'EOF') {
			if (this.currentToken.type === 'COMMA') {
				this.advance();
				continue;
			}

			if (this.currentToken.type === 'LEFT_BRACE') {
				const fragment = this.parseContentNode();
				if (fragment && typeof fragment === 'object') {
					this.tryAppendNodeToContent(rootNode.type, fragment, allContent, isValidChild, {
						topNodeType: rootNode.type,
					});
				}
			} else {
				this.advance();
			}
		}

		// Update content if we added more nodes
		if (allContent.length > 0 || Array.isArray(rootNode.content)) {
			rootNode.content = allContent;
		}

		return rootNode;
	}
}
